import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { archiveStateExhibit, getProviderExhibitSignatures, getStateExhibitTemplate, renewStateExhibit, RenewStateExhibitParams, signStateExhibit, SignStateExhibitParams } from '@uplift/api';
import { BaseQueryOpts } from './baseTypes';
import { providerQueryKeys } from './provider/providerQueryKeys';


export const stateExhibitQueryKeys = {
  template: (providerId: string, licenseId: string) => [ 'stateExhibit', 'template', providerId, licenseId ] as const,
  signatures: (providerId: string) => [ 'provider', providerId, 'stateExhibitSignatures' ] as const,
};


export const useStateExhibitTemplateQuery = (providerId: string, licenseId: string) => {
  return useSuspenseQuery({
    queryKey: stateExhibitQueryKeys.template(providerId, licenseId),
    queryFn: async () => {
      try {
        const response = await getStateExhibitTemplate(providerId, licenseId);

        return response.data;
      } catch (err) {
        if (isAxiosError(err) && err.status === 404) {
          return null;
        }
        throw err;
      }
    },
  });
};


export const useProviderExhibitSignaturesQuery = (providerId: string, opts?: BaseQueryOpts) => {
  return useQuery({
    queryKey: stateExhibitQueryKeys.signatures(providerId),
    queryFn: async () => {
      const response = await getProviderExhibitSignatures(providerId);

      return response.data;
    },
    ...opts,
  });
};


export const useSignStateExhibitMutation = (providerId: string, licenseId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: SignStateExhibitParams) => signStateExhibit(providerId, licenseId, params),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: stateExhibitQueryKeys.signatures(providerId) }),
        queryClient.invalidateQueries({ queryKey: providerQueryKeys.statesSelection(providerId) }),
        queryClient.invalidateQueries({ queryKey: providerQueryKeys.licenses(providerId) }),
        queryClient.invalidateQueries({ queryKey: [ 'user', providerId, 'documents' ] }),
      ]);
    },
    onError: async (err) => {
      if (isAxiosError(err) && err.status === 409) {
        await queryClient.invalidateQueries({ queryKey: stateExhibitQueryKeys.template(providerId, licenseId) });

        throw new Error('This state exhibit has been updated, please review and sign again');
      }
    },
  });
};


export const useRenewStateExhibitMutation = (providerId: string, licenseId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: RenewStateExhibitParams) => renewStateExhibit(providerId, licenseId, params),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: stateExhibitQueryKeys.signatures(providerId) }),
        queryClient.invalidateQueries({ queryKey: providerQueryKeys.statesSelection(providerId) }),
        queryClient.invalidateQueries({ queryKey: providerQueryKeys.licenses(providerId) }),
        queryClient.invalidateQueries({ queryKey: [ 'user', providerId, 'documents' ] }),
      ]);
    },
    onError: async (err) => {
      if (isAxiosError(err) && err.status === 409) {
        await queryClient.invalidateQueries({ queryKey: stateExhibitQueryKeys.template(providerId, licenseId) });

        throw new Error('This state exhibit has been updated, please review and sign again');
      }
    },
  });
};


export const useArchiveStateExhibitMutation = (providerId: string, licenseId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => archiveStateExhibit(licenseId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: stateExhibitQueryKeys.signatures(providerId) }),
        queryClient.invalidateQueries({ queryKey: providerQueryKeys.statesSelection(providerId) }),
        queryClient.invalidateQueries({ queryKey: providerQueryKeys.licenses(providerId) }),
        queryClient.invalidateQueries({ queryKey: [ 'user', providerId, 'documents' ] }),
      ]);
    },
  });
};
